<template>
    <BaseForm>
        <template #fields>
            <InlineFieldGroup>
                <TextField
                    class="w-1/3"
                    :value="values.partnersOverviewName"
                    :label="$t('partners-overview-name')"
                    :error-messages="errors.partnersOverviewName"
                    @input="setFieldValue('partnersOverviewName', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('partnersOverviewName', true)"
                />
                <TextField
                    class="w-2/3"
                    :value="values.partnersOverviewLink"
                    :label="$t('partners-overview-link')"
                    :error-messages="errors.partnersOverviewLink"
                    @input="setFieldValue('partnersOverviewLink', ($event.target as HTMLInputElement).value)"
                    @blur="setFieldTouched('partnersOverviewLink', true)"
                />
            </InlineFieldGroup>
            <MultiLinkGroup>
                <MultiLink
                    ref="organizersMultiLink"
                    :label="$t('organizers')"
                    :links="values.organizers"
                    :error-messages="errors.organizers"
                    @input="setFieldValue('organizers', $event)"
                    @delete="setFieldValue('organizers', $event)"
                />
                <MultiLink
                    ref="executorsMultiLink"
                    :label="$t('executors')"
                    :links="values.executors"
                    :error-messages="errors.executors"
                    @input="setFieldValue('executors', $event)"
                    @delete="setFieldValue('executors', $event)"
                />
                <MultiLink
                    ref="sponsorsMultiLink"
                    :label="$t('sponsors')"
                    :links="values.sponsors"
                    :error-messages="errors.sponsors"
                    @input="setFieldValue('sponsors', $event)"
                    @delete="setFieldValue('sponsors', $event)"
                />
            </MultiLinkGroup>
            <TextAreaField
                :value="values.additionalPartners"
                :label="$t('additional-partners')"
                :error-messages="errors.additionalPartners"
                :max-length="EventSeriesPartnersSchema.shape.additionalPartners.maxLength ?? undefined"
                @input="setFieldValue('additionalPartners', ($event.target as HTMLInputElement).value)"
                @blur="setFieldTouched('additionalPartners', true)"
            />
        </template>
    </BaseForm>
</template>
<script setup lang="ts">
import BaseForm from '@/components/Form/BaseForm.vue';
import InlineFieldGroup from '@/components/Form/InlineFieldGroup.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import TextField from '@/components/Form/TextField.vue';
import { useMultiLink } from '@/components/MultiLink/composables/useMultiLink';
import MultiLink from '@/components/MultiLink/MultiLink.vue';
import MultiLinkGroup from '@/components/MultiLink/MultiLinkGroup.vue';
import { useEventSeriesInjection } from '@/event/composables/useEventSeriesInjection';
import { useForm } from '@/forms/composables/useForm';
import { computed } from 'vue';
import { EventSeriesPartnersSchema } from '../schemas';

const props = defineProps<{ seriesId: string }>();

const seriesId = computed(() => props.seriesId);
const { series, updateEventSeries, createEventSeriesMultiLink } = useEventSeriesInjection();
const { getFormFieldLinks, saveMultiLinkFields } = useMultiLink(createEventSeriesMultiLink);

const organizersId = computed(() => series.value?.organizers?.id);
const executorsId = computed(() => series.value?.executors?.id);
const sponsorsId = computed(() => series.value?.sponsors?.id);

const organizersLinks = computed(() => series.value?.organizers?.links.nodes ?? []);
const executorsLinks = computed(() => series.value?.executors?.links.nodes ?? []);
const sponsorsLinks = computed(() => series.value?.sponsors?.links.nodes ?? []);

const { errors, values, setFieldValue, setFieldTouched, validate, onDiscard, resetForm } = useForm(
    EventSeriesPartnersSchema,
    computed(() => ({
        partnersOverviewLink: series.value?.partnersOverviewLink,
        partnersOverviewName: series.value?.partnersOverviewName,
        organizers: getFormFieldLinks(organizersLinks),
        executors: getFormFieldLinks(executorsLinks),
        sponsors: getFormFieldLinks(sponsorsLinks),
        additionalPartners: series.value?.additionalPartners,
    })),
);

async function save() {
    const valid = await validate();
    if (valid && series.value) {
        await updateEventSeries({ id: seriesId.value, ...values.value });
        await saveMultiLinkFields([
            { id: organizersId.value, fieldName: 'organizers', links: values.value.organizers },
            { id: executorsId.value, fieldName: 'executors', links: values.value.executors },
            { id: sponsorsId.value, fieldName: 'sponsors', links: values.value.sponsors },
        ]);
    }
}

defineExpose({ save, onDiscard, resetForm });
</script>
